import React, { useState, useRef, useEffect } from 'react'
import { FirebaseStorageService } from '../services/FirebaseStorageService'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from './Toast'
import LoadingSpinner from './LoadingSpinner'
import heic2any from 'heic2any'
import './ImageUpload.css'

interface ImageUploadProps {
  onImagesUploaded: (imageUrls: string[]) => void
  maxImages?: number
}

const ImageUpload: React.FC<ImageUploadProps> = ({ onImagesUploaded, maxImages = 10 }) => {
  const { user } = useAuth()
  const { showToast } = useToast()
  const [uploadedImages, setUploadedImages] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [converting, setConverting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Load existing images when component mounts
  useEffect(() => {
    const loadExistingImages = async () => {
      if (!user) return

      try {
        const imageUrls = await FirebaseStorageService.getUserImages(user.id)

        if (imageUrls.length > 0) {
          setUploadedImages(imageUrls)
          onImagesUploaded(imageUrls)
        }
      } catch (error) {
        console.error('Error loading existing images:', error)
      }
    }

    loadExistingImages()
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  // Check if file is HEIC format
  const isHEIC = (file: File): boolean => {
    const fileName = file.name.toLowerCase()

    // Simple and reliable HEIC detection - only check file extension
    return fileName.endsWith('.heic') || fileName.endsWith('.heif')
  }

  // Convert HEIC to JPEG
  const convertHEICToJPEG = async (file: File): Promise<File> => {
    try {
      console.log('Starting HEIC conversion for:', file.name, 'Size:', file.size, 'Type:', file.type)

      // Validate file size
      if (file.size === 0) {
        throw new Error('HEIC file appears to be empty or corrupted')
      }

      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        throw new Error('HEIC file is too large (max 50MB)')
      }

      const result = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.8
      })

      console.log('HEIC conversion result:', result)

      // heic2any can return either a Blob or an array of Blobs
      const blob = Array.isArray(result) ? result[0] : result

      if (!blob || blob.size === 0) {
        throw new Error('Conversion resulted in empty file')
      }

      // Create a new File object from the converted blob
      const convertedFile = new File(
        [blob],
        file.name.replace(/\.(heic|heif)$/i, '.jpg'),
        { type: 'image/jpeg' }
      )

      console.log('HEIC conversion successful. New file:', convertedFile.name, 'Size:', convertedFile.size)
      return convertedFile
    } catch (error) {
      console.error('HEIC conversion failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Conversion failed'
      console.error('Error details:', errorMessage)

      // Provide more helpful error messages
      if (errorMessage.includes('Could not parse HEIF file')) {
        throw new Error('HEIC file is corrupted or in an unsupported format. Please try taking a new photo.')
      }

      throw new Error(`HEIC conversion failed: ${errorMessage}. Try uploading a JPG or PNG instead.`)
    }
  }

  // Process file (convert HEIC if needed)
  const processFile = async (file: File): Promise<File> => {
    console.log('Processing file:', file.name, 'Type:', file.type, 'HEIC detected:', isHEIC(file))

    if (isHEIC(file)) {
      console.log('Converting HEIC file:', file.name)
      return await convertHEICToJPEG(file)
    }
    return file
  }

  const uploadImage = async (originalFile: File): Promise<string> => {
    if (!user) throw new Error('User not authenticated')

    // Process file (convert HEIC if needed)
    const processedFile = await processFile(originalFile)

    // Upload using Firebase Storage Service
    return await FirebaseStorageService.uploadImage(user.id, processedFile)
  }

  const handleFileSelect = async (files: FileList) => {
    if (!files.length) return

    const remainingSlots = maxImages - uploadedImages.length
    const filesToUpload = Array.from(files).slice(0, remainingSlots)

    if (filesToUpload.length === 0) {
      showToast(`You can only upload up to ${maxImages} images total.`, 'warning')
      return
    }

    // Check if any files need conversion
    const hasHEICFiles = filesToUpload.some(file => isHEIC(file))

    if (hasHEICFiles) {
      setConverting(true)
    }
    setUploading(true)

    try {
      const uploadPromises = filesToUpload.map(uploadImage)
      const newImageUrls = await Promise.all(uploadPromises)

      const updatedImages = [...uploadedImages, ...newImageUrls]
      setUploadedImages(updatedImages)
      onImagesUploaded(updatedImages)
    } catch (error) {
      console.error('Error uploading images:', error)

      // More specific error messages
      if (error instanceof Error && error.message.includes('convert HEIC')) {
        showToast('Error converting HEIC images. Please try uploading JPG or PNG files instead.', 'error')
      } else {
        showToast('Error uploading images. Please try again.', 'error')
      }
    } finally {
      setUploading(false)
      setConverting(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)

    const files = e.dataTransfer.files
    handleFileSelect(files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  const removeImage = async (index: number) => {
    const imageUrl = uploadedImages[index]

    try {
      // Remove from Firebase Storage and database
      if (user) {
        await FirebaseStorageService.deleteImage(imageUrl, user.id)
      }

      // Remove from state
      const updatedImages = uploadedImages.filter((_, i) => i !== index)
      setUploadedImages(updatedImages)
      onImagesUploaded(updatedImages)
    } catch (error) {
      console.error('Error removing image:', error)
    }
  }

  return (
    <div className="image-upload">
      <h3>Upload Your Ingredient Photos</h3>
      <p>Upload up to {maxImages} photos of your fridge, pantry, or ingredients ({uploadedImages.length}/{maxImages})</p>

      <div className="upload-tips">
        <h4>📸 Tips for Better Detection:</h4>
        <ul className="tips-grid">
          <li className="tip-item">
            <span className="tip-icon" aria-hidden="true">✅</span>
            <span>Take multiple angles: shelves, door, drawers separately</span>
          </li>
          <li className="tip-item">
            <span className="tip-icon" aria-hidden="true">✅</span>
            <span>Ensure good lighting - avoid shadows</span>
          </li>
          <li className="tip-item">
            <span className="tip-icon" aria-hidden="true">✅</span>
            <span>Move items to show labels clearly</span>
          </li>
          <li className="tip-item">
            <span className="tip-icon" aria-hidden="true">❌</span>
            <span>Avoid blurry or dark photos</span>
          </li>
        </ul>
      </div>

      <div
        className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            fileInputRef.current?.click()
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Upload ingredient photos. ${uploadedImages.length} of ${maxImages} images uploaded. Click or press Enter to select files.`}
      >
        {uploading ? (
          converting ? (
            <LoadingSpinner message="Converting HEIC images..." size="small" />
          ) : (
            <LoadingSpinner message="Uploading images..." size="small" />
          )
        ) : (
          <div className="upload-content">
            <div className="upload-icon">📸</div>
            <p>Drag & drop images here or click to select</p>
            <p className="upload-hint">Supports JPG, PNG, HEIC formats</p>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.heic,.heif"
        onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
        style={{ display: 'none' }}
      />

      {uploadedImages.length > 0 && (
        <div className="uploaded-images">
          <h4>Uploaded Images</h4>
          <div className="image-grid" role="list" aria-label="Uploaded ingredient photos">
            {uploadedImages.map((imageUrl, index) => (
              <div key={index} className="image-item" role="listitem">
                <img src={imageUrl} alt={`Uploaded ingredient ${index + 1} of ${uploadedImages.length}`} />
                <button
                  className="remove-button"
                  onClick={() => removeImage(index)}
                  type="button"
                  aria-label={`Remove ingredient photo ${index + 1}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageUpload